import { InputOption } from "../../../../web-components/src/atoms/input/types";
import {
  useProcessTableColumns,
  useProcessTableFilters,
  useProcessTableGroupBy,
  useProcessTableSort,
  useProcessTableState,
  useProcessTableTimeFrame,
} from "../../../context/ProcessTableContext";
import GeneralModal from "../../GeneralModal";
import { CreateProcessViewFormInput } from "@avenueops/shared-types";
import { SmartForm } from "@avenueops/web-components";
import { zodResolver } from "@hookform/resolvers/zod";
import EmailMultiInput from "components/inputs/EmailMultiInput";
import React, { useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import { toastError } from "utils/toast";
import { trpc } from "utils/trpc";

interface Props {
  setModalVisible: (_: boolean) => void;
  modalVisible: boolean;
  starterTitle?: string;
  starterCsvRecipients?: string[];
  id?: string;
}

export default function SignalViewModal({ setModalVisible, modalVisible, starterTitle, starterCsvRecipients, id }: Props) {
  const [filters, setFilters] = useProcessTableFilters();
  const [groupBy, setGroupBy] = useProcessTableGroupBy();
  const [columns, setColumns] = useProcessTableColumns();
  const [sort, setSort] = useProcessTableSort();
  const [processTableState, setProcessTableState] = useProcessTableState();

  const [timeFrameArgs, setTimeFrame] = useProcessTableTimeFrame();
  const utils = trpc.useContext();

  const { data: organizationMembers } = trpc.organizationMemberships.list.useQuery();

  const getEmailOptions: () => InputOption<string>[] = () => {
    const emails = organizationMembers?.map((member: any) => {
      return { label: member.user.email, value: member.user.email };
    });

    return emails ?? [];
  };

  const defaultValues = {
    title: starterTitle ?? "",
    csvExportEmailRecipients: starterCsvRecipients ?? [],
  };

  const formMethods = useForm({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(CreateProcessViewFormInput),
  });

  const { handleSubmit, register, control, formState } = formMethods;

  const { mutateAsync: updateView, isLoading: updateLoading } = trpc.processViews.update.useMutation({
    onSuccess: async () => {
      await utils.processViews.list.invalidate();
      await utils.processViews.get.invalidate({ processViewId: id! });
      setModalVisible(false);
      toast.success("View was successfully updated!");
    },
  });

  const { mutateAsync: createView, isLoading } = trpc.processViews.create.useMutation({
    onSuccess: async () => {
      await utils.processViews.list.invalidate();
      setModalVisible(false);
      toast.success("View was successfully created!");
    },
  });

  const onCreate = useCallback(
    async (data: any) => {
      try {
        if (id) {
          await updateView({
            processViewId: id,
            ...data,
            filters: filters ?? [],
            sort: sort ?? [],
            groupBy: groupBy ?? "",
            ...timeFrameArgs,
            columns: columns ?? [],
          });
        } else {
          await createView({
            ...data,
            filters: filters ?? [],
            sort: sort ?? [],
            groupBy: groupBy ?? "",
            ...timeFrameArgs,
            columns: columns ?? [],
          });
        }
      } catch (error) {
        toastError(error, "There was an error creating this view. Please try again later.");
      }
    },
    [updateView, createView, processTableState],
  );

  return (
    <FormProvider {...formMethods}>
      <GeneralModal
        noMaxWidth
        isLoading={isLoading}
        setModalVisible={setModalVisible}
        modalVisible={modalVisible}
        title={id ? "Update View" : "Create View"}
        description="Find all views under Signals in the side navigation menu."
        onConfirm={handleSubmit(onCreate)}
      >
        <div className="flex flex-col gap-y-4 w-full">
          <SmartForm.TextInput title="Title" name="title" />
          <EmailMultiInput
            title="Automated CSV Email Recipient (optional)"
            required={false}
            name="csvExportEmailRecipients"
            control={control}
            options={getEmailOptions() ?? []}
          />
        </div>
      </GeneralModal>
    </FormProvider>
  );
}
